// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { DynamicLabelingArticleConnectProcessor } from '../../article/processor/prepare/connect/dynamic-labeling-article-connect.processor';
import { ProjectModel } from '../../../model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LabelStrip } from './replace-labeling-data';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';

export class ReplaceLabelingArticleCreator {
  private _isActive: boolean = false;

  private _loadingArticleWrapperGUIDs: string[] = [];
  private _articleContainerWrappersMap: Dictionary<string, ArticleContainersWrapper> = new Dictionary<string, ArticleContainersWrapper>();
  private _articleWrapperGuidToDefaultConnection: Dictionary<string, ConnectionAreaVO> = new Dictionary<string, ConnectionAreaVO>();

  constructor(
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private articleManager: ArticleManager,
    private dynamicLabelingArticleConnectProcessor: DynamicLabelingArticleConnectProcessor
  ) { }

  private getCreatedLabelingArticleContainers(): ArticleContainer[] {
    if (!this._articleContainerWrappersMap) {
      return [];
    }

    const articleContainers: ArticleContainer[] = [];
    for (const articleWrapper of this._articleContainerWrappersMap.values()) {
      articleContainers.push(...articleWrapper.articleContainers);
    }
    return articleContainers;
  }

  public async createLabelingArticles(labelStrips: LabelStrip[]): Promise<ArticleContainer[]> {
    this._isActive = true;

    const articleWrappersToRefresh: ArticleContainersWrapper[] = [];
    for (const labelStrip of labelStrips) {
      const originalUsage: ArticleUsageVO = this.projectModel.getArticleUsage(labelStrip.originalUsageId);
      if (!originalUsage) {
        // create new labeling articles if original article was already removed
        const articleWrappers: ArticleContainersWrapper[] = this.prepareLabelingArticleWrapper(labelStrip);
        if (ArrayUtils.isNotEmpty(articleWrappers)) {
          for (const articleWrapper of articleWrappers) {
            articleWrappersToRefresh.push(articleWrapper);
          }
        }
      }
    }
    await this.refreshArticleWrappers(articleWrappersToRefresh);
    return this.getCreatedLabelingArticleContainers();
  }

  private prepareLabelingArticleWrapper(labelStrip: LabelStrip): ArticleContainersWrapper[] {
    let articleWrappers: ArticleContainersWrapper[] = [];

    const defaultArticle: ArticleVO = this.articleManager.getCachedArticle(labelStrip.articleId, labelStrip.articleVersion);
    if (defaultArticle && labelStrip.isConnectable()) {
      articleWrappers = this.dynamicLabelingArticleConnectProcessor.preProcessWithoutRefresh(
        defaultArticle,
        labelStrip.getDirectAcceptingConnections()
      );
      for (const articleWrapper of articleWrappers) {
        const articleUsage: ArticleUsageVO = this.projectArticleService.createNewArticleUsage(defaultArticle.identity, defaultArticle.version);
        labelStrip.newUsageIds.push(articleUsage.id);
        articleWrapper.articleUsageID = articleUsage.id;

        this._loadingArticleWrapperGUIDs.push(articleWrapper.wrapperGUID);
        this._articleContainerWrappersMap.setValue(articleWrapper.wrapperGUID, articleWrapper);
        this._articleWrapperGuidToDefaultConnection.setValue(
          articleWrapper.wrapperGUID,
          ArticleUtils.findFirstConnectionArea(defaultArticle.metaElements, ConnectionAreaVO.SIDE_GIVING)
        );
      }
    }
    return articleWrappers;
  }

  private async refreshArticleWrappers(articleWrappers: ArticleContainersWrapper[]): Promise<void> {
    for (const articleWrapper of articleWrappers) {
      try {
        await articleWrapper.refreshWrapper();
        this.articleWrapperCreated(articleWrapper.wrapperGUID);
      } catch(error) {
        Log.error('Could not refresh article wrapper: ', error);
      }
    }
  }

  private articleWrapperCreated(wrapperGuid: string): boolean {
    if (!this._isActive) {
      return;
    }
    if (this._loadingArticleWrapperGUIDs.indexOf(wrapperGuid) === -1) {
      return;
    }

    this._loadingArticleWrapperGUIDs.splice(this._loadingArticleWrapperGUIDs.indexOf(wrapperGuid), 1);

    const articleWrapper: ArticleContainersWrapper = this._articleContainerWrappersMap.getValue(wrapperGuid);
    const givingArea: ConnectionAreaVO = this._articleWrapperGuidToDefaultConnection.getValue(wrapperGuid);
    if (articleWrapper && givingArea) {
      this.dynamicLabelingArticleConnectProcessor.process(
        articleWrapper.articleContainers,
        articleWrapper.baseArticleContainer,
        [givingArea],
        NaN,
        false
      );
    }

    if (ArrayUtils.isEmpty(this._loadingArticleWrapperGUIDs)) {
      return true;
    }
  }

  public clear(): void {
    this._isActive = false;
    this._articleContainerWrappersMap = new Dictionary<string, ArticleContainersWrapper>();
    this._articleWrapperGuidToDefaultConnection = new Dictionary<string, ConnectionAreaVO>();
    this._loadingArticleWrapperGUIDs = [];
  }
}
